import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, User as UserIcon, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// Minimal profile page — nickname management. The nickname is the SINGLE
// public identity used across the leaderboard, so we enforce it before play.
export default function Profile() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const next = params.get("next") || "/";
  // When the user lands here because the global RequireNickname guard kicked
  // in, we surface a stronger "first-time setup" headline + a polite reason.
  const isFirstSetup = params.get("need") === "nickname";

  useEffect(() => {
    if (user === false) {
      navigate("/login?next=/profile");
      return;
    }
    if (user && user !== null) {
      setNickname(user.nickname || "");
    }
  }, [user, navigate]);

  if (!user || user === false) {
    return null;
  }

  // Client-side validation mirrored from backend NICK_RE so we can give
  // instant feedback without a roundtrip. Backend remains authoritative.
  const trimmed = nickname.trim();
  const NICK_RE = /^[A-Za-zÀ-ÿ0-9 _\-]{3,20}$/;
  const clientError = (() => {
    if (!trimmed) return "";
    if (trimmed.length < 3) return "Le pseudo doit contenir au moins 3 caractères.";
    if (trimmed.length > 20) return "Le pseudo doit contenir maximum 20 caractères.";
    if (!NICK_RE.test(trimmed)) return "Ce pseudo contient des caractères non autorisés.";
    return "";
  })();
  const canSubmit = !loading && trimmed.length >= 3 && !clientError;

  const save = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      await api.put("/me/profile/nickname", { nickname: trimmed });
      await refresh();
      toast.success("Pseudo enregistré");
      // Decode the "next" path if it was URL-encoded by RequireNickname.
      let target = next;
      try {
        target = decodeURIComponent(next);
      } catch (_) {
        target = next;
      }
      navigate(target || "/");
    } catch (err) {
      // Surface the server message inline under the field (not a toast) so
      // the user keeps their input and sees exactly why it failed.
      const msg = formatApiError(err) || "Impossible d'enregistrer le pseudo pour le moment. Merci de réessayer.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const shownError = error || clientError;

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/15 via-transparent to-fuchsia-500/10" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Zap className="w-6 h-6 text-cyan-400" />
          <span className="font-display font-black uppercase tracking-tighter text-xl">
            Otra<span className="text-cyan-400 neon-text-primary">Quizz</span>
          </span>
        </div>
        <form
          onSubmit={save}
          className="panel rounded-3xl p-8 space-y-5"
          data-testid="profile-form"
        >
          <div>
            {isFirstSetup ? (
              <>
                <span
                  data-testid="profile-first-setup-kicker"
                  className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat block mb-2"
                >
                  Bienvenue · 1 / 1
                </span>
                <h1
                  data-testid="profile-title"
                  className="font-display text-3xl font-bold"
                >
                  Choisis ton pseudo
                </h1>
                <p className="text-white/55 text-sm mt-1">
                  Ton pseudo apparaîtra dans tous les classements. Définis-le
                  maintenant — tu n&apos;auras plus à le saisir avant chaque défi.
                </p>
              </>
            ) : (
              <>
                <h1
                  data-testid="profile-title"
                  className="font-display text-3xl font-bold"
                >
                  Ton pseudo
                </h1>
                <p className="text-white/55 text-sm mt-1">
                  Ton pseudo est lié à ton compte. Il s&apos;affiche dans le
                  classement à chaque participation.
                </p>
              </>
            )}
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-white/50 font-mono-stat block mb-1.5">
              Pseudo (3-20 caractères)
            </span>
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-black/40 border transition-all ${shownError ? "border-rose-400/60" : "border-white/10 focus-within:border-cyan-400/60 focus-within:glow-primary"}`}>
              <UserIcon className="w-4 h-4 text-cyan-300" />
              <input
                data-testid="profile-nickname-input"
                value={nickname}
                onChange={(e) => { setError(""); setNickname(e.target.value.slice(0, 20)); }}
                placeholder="Ton nom de guerrier"
                className="w-full bg-transparent outline-none font-display text-lg text-white"
                autoFocus
                maxLength={20}
                autoComplete="off"
              />
            </div>
            {shownError && (
              <p data-testid="profile-nickname-error" className="mt-2 text-sm text-rose-300 font-mono-stat">
                {shownError}
              </p>
            )}
          </label>
          <button
            data-testid="profile-save-button"
            type="submit"
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all glow-primary"
          >
            {loading ? "..." : isFirstSetup ? "Continuer" : "Enregistrer"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>
          {isFirstSetup && (
            <p className="text-xs text-white/40 text-center font-mono-stat uppercase tracking-widest">
              Ce pseudo sera ton identité publique
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
